import {
  AggsResult,
  RecommendResult,
  SearchAggsResponse,
  SearchAutoCompleteResponse,
  SearchConfigResponse,
  SearchFilterNameResponse,
  SearchIssueResponse,
  SearchProductResponse,
  SearchRecommendEventResponse,
  SearchRecommendResponse,
  SearchResultResponse,
  SearchReviewResponse,
} from '@wconcept/wconcept-common/models/display/search';
import { CommonResponse } from '@models/api';

import {
  MolocoAdProductRespose,
  RequestMolocoAdProductsParam,
} from '@models/moloco';
import { getCookieWckDisplayMktSID } from '@wconcept/wconcept-common/utils/cookie';
import { getMolocoCustNo } from '@utils/domainUtil';
import { SrchResultExclusivePrdResult } from '@models/search';
import type {
  LpProductFilterType,
  LpSearchProductRequest,
} from '@apis/renewal/lpProduct';
import { get, post } from '../api';
import { ApiTypes } from './apiType';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_BASE_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_KEY || '',
  },
};
const gatewayBaseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const gatewayOptions = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

/**
 * 통합 검색 LP 상품 API v1
 * 초기 데이터, 상품 목록, 상품 수, 필터를 조회한다.
 */
const postSearchLpProduct = async <TResponse>(
  url: string,
  params: LpSearchProductRequest
): Promise<TResponse | undefined> => {
  const { data } = await post<
    CommonResponse<TResponse>,
    LpSearchProductRequest
  >({
    url,
    baseUrl: gatewayBaseURL,
    options: gatewayOptions,
    data: params,
  });

  return data;
};

export const requestSearchLpProductInit = async <TResponse>(
  params: LpSearchProductRequest
): Promise<TResponse | undefined> =>
  postSearchLpProduct('/display/api/v1/search/result/init', params);

export const requestSearchLpProduct = async <TResponse>(
  params: LpSearchProductRequest
): Promise<TResponse | undefined> =>
  postSearchLpProduct('/display/api/v1/search/result/product', params);

export const requestSearchLpProductCount = async <TResponse>(
  params: LpSearchProductRequest
): Promise<TResponse | undefined> =>
  postSearchLpProduct('/display/api/v1/search/result/product/count', params);

export const requestSearchLpProductFilter = async <TResponse>(
  type: LpProductFilterType,
  params: LpSearchProductRequest
): Promise<TResponse | undefined> =>
  postSearchLpProduct(
    `/display/api/v1/search/result/product/filter/${type}`,
    params
  );
/**
 * 검색 자동완성
 */
export const GET_SEARCH_AUTO_COMPLETE_API = (keyword: string) =>
  `${ApiTypes.SEARCH}/auto-complete?keyword=${encodeURIComponent(keyword)}`;

export const requestAutoComplete = async (keyword: string, token?: any) => {
  const { data } = await get<SearchAutoCompleteResponse>({
    url: GET_SEARCH_AUTO_COMPLETE_API(keyword),
    baseUrl: baseURL,
    options,
  });
  return data;
};

/**
 * 검색 결과 조회.
 */
export const GET_SEARCH_RESULT_API = ({
  gender,
  keyword,
  domainType,
}: {
  gender: string;
  keyword: string;
  domainType: string;
}) =>
  `${ApiTypes.SEARCH}/result/${gender}?keyword=${encodeURIComponent(
    keyword
  )}&domainType=${domainType}`;

export const requestSearchResult = async ({
  gender,
  keyword,
  domainType,
}: {
  gender: string;
  keyword: string;
  domainType: string;
}) => {
  const { data } = await get<SearchResultResponse>({
    url: GET_SEARCH_RESULT_API({ gender, keyword, domainType }),
    baseUrl: baseURL,
    options,
  });
  return data;
};
// export const requestSearchResult = async ({
//   gender,
//   keyword,
//   domainType,
// }: {
//   gender: string;
//   keyword: string;
//   domainType: string;
// }) => {
//   const { data } = await axiosDisplay.get<SearchResultResponse>(
//     GET_SEARCH_RESULT_API({ gender, keyword, domainType })
//   );
//   return data;
// };

/**
 * 이벤트 결과 조회
 */
interface ReqeustIssueResultPrams {
  keyword: string;
  gender: string;
  pageNo: number;
  pageSize: number;
  source: string;
  device: string;
  searchType: string;
}

export const GET_SEARCH_ISSUE_RESULT_API = () =>
  `${ApiTypes.SEARCH}/result/issue`;

export const requestIssueResult = async ({
  keyword,
  gender,
  pageNo,
  pageSize,
  source,
  device,
  searchType,
}: ReqeustIssueResultPrams) => {
  const { data } = await post<SearchIssueResponse>({
    url: GET_SEARCH_ISSUE_RESULT_API(),
    baseUrl: baseURL,
    options,
    data: {
      keyword,
      gender,
      pageNo,
      pageSize,
      source,
      device,
      searchType,
    },
  });
  return data;
};

/**
 * 리뷰 결과 조회.
 */
interface RequestReviewResultParams {
  keyword: string;
  gender: string;
  pageNo: number;
  pageSize: number;
  source: string;
  device?: string;
  searchType: string;
}

export const GET_SEARCH_REVIEW_RESULT_API = () =>
  `${ApiTypes.SEARCH}/result/review`;

export const requestReviewResult = async ({
  keyword,
  gender,
  pageNo,
  pageSize,
  source,
  device,
  searchType,
}: RequestReviewResultParams) => {
  const { data } = await post<SearchReviewResponse>({
    url: GET_SEARCH_REVIEW_RESULT_API(),
    baseUrl: baseURL,
    options,
    data: {
      keyword,
      gender,
      pageNo,
      pageSize,
      source,
      device,
      searchType,
    },
  });

  return data;
};

/**
 * 오탈자 교정
 */
// export const GET_SEARCH_SPLLER_API = () => {
//   return `${ApiTypes.SEARCH}/speller`;
// };

// export const requestSearchSpller = async () => {
//   const { data } = await axiosDisplay.get<any>(GET_SEARCH_SPLLER_API());
//   return data;
// };

/**
 *  검색 Config 조회
 */
export const GET_SEARCH_CONFIG_API = () => `/display/api/v2/search/config`;

export const requestSearchConfig = async () => {
  const { data } = await get<SearchConfigResponse>({
    url: GET_SEARCH_CONFIG_API(),
    baseUrl: baseURL,
    options,
  });
  return data;
};

/**
 * 상품 결과값
 */
export interface RequestSearchAccParams {
  gender?: string;
  domainType?: string;
  keyword: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  sq?: null;
  bcds?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  priceSections?: null;
  minPrice?: string | number;
  maxPrice?: string | number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
  aggs?: string[];
  source: string;
  device: 'Android' | 'iOS' | 'PC' | 'MO' | string;
  searchType?:
    | 'popular'
    | 'related'
    | 'recent'
    | 'direct'
    | 'ac_key'
    | 'ac_key_brand'
    | string;
  recommendItems: string[];
  shopCds?: string[];
  dcRateGroup?: string[];
  styles?: string[];
}

/**
 * 상품 결과값
 */
export interface RequestSearchFilterNameParams {
  custNo: string;
  gender: string;
  keyword: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  sq?: null;
  bcds?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  priceSections?: null;
  minPrice?: string | number;
  maxPrice?: string | number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
  aggs?: string[];
  source: string;
  device: 'Android' | 'iOS' | 'PC' | 'MO' | string;
  searchType?:
    | 'popular'
    | 'related'
    | 'recent'
    | 'direct'
    | 'ac_key'
    | 'ac_key_brand'
    | string;
  recommendItems: string[];
}

export const GET_SEARCH_PRODCT_LIST_API = () =>
  `${ApiTypes.SEARCH}/result/product`;

export const requestSearchProdcutList = async ({
  custNo,
  gender,
  keyword,
  sort,
  pageNo,
  pageSize,
  sq,
  bcds,
  mcd,
  ccds,
  pcds,
  lcds,
  priceSections,
  minPrice,
  maxPrice,
  colors,
  benefits,
  discounts,
  status,
  saleTags,
  source,
  device,
  searchType,
}: RequestSearchProdcutListParams) => {
  const { data } = await post<SearchProductResponse>({
    url: GET_SEARCH_PRODCT_LIST_API(),
    baseUrl: baseURL,
    options,
    data: {
      custNo,
      gender,
      keyword,
      sort,
      pageNo,
      pageSize,
      sq,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      source,
      device,
      searchType,
    },
  });

  // const { data } = await axiosDisplay.post<SearchProductResponse>(
  //   GET_SEARCH_PRODCT_LIST_API(),
  //   {
  //     custNo,
  //     gender,
  //     keyword,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     sq,
  //     bcds,
  //     mcd,
  //     ccds,
  //     pcds,
  //     lcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     source,
  //     device,
  //     searchType,
  //   }
  // );
  return data;
};

export const GET_SEARCH_PRODCT_AGGS = () =>
  `${ApiTypes.SEARCH}/result/product/aggs`;

export const requestSearchProdcutAggs = async ({
  gender,
  keyword,
  sort,
  pageNo = 1,
  pageSize,
  sq,
  bcds,
  mcd,
  ccds,
  pcds,
  lcds,
  priceSections,
  minPrice,
  maxPrice,
  colors,
  benefits,
  discounts,
  status,
  saleTags,
  source,
  device,
  searchType,
  recommendItems,
  aggs = [
    'brand',
    'topBrand',
    'newBrand',
    'category',
    'status',
    'discounts',
    'benefits',
    'recoCategory',
    'colors',
    'saleTag',
    'priceRange',
  ],
}: RequestSearchAccParams) => {
  const { data } = await get<
    CommonResponse<AggsResult>,
    RequestSearchAccParams
  >({
    url: GET_SEARCH_PRODCT_AGGS(),
    baseUrl: baseURL,
    options,
    params: {
      gender,
      keyword,
      sort,
      pageNo,
      pageSize,
      sq,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      aggs,
      source,
      device,
      searchType,
      recommendItems,
    },
  });
  // const { data } = await axiosDisplay.post<SearchAggsResponse>(
  //   GET_SEARCH_PRODCT_AGGS(),
  //   {
  //     gender,
  //     keyword,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     sq,
  //     bcds,
  //     mcd,
  //     ccds,
  //     pcds,
  //     lcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     aggs,
  //     source,
  //     device,
  //     searchType,
  //     recommendItems,
  //   }
  // );
  return data as AggsResult;
};

const GET_SEARCH_RECOMMAND_LIST = () =>
  `${ApiTypes.SEARCH}/result/product/recommend`;

export const requestSearchRcommendList = async (
  recommendItems: string[],
  gender: string,
  keyword: string,
  custNo: string
) => {
  const { data } = await post<SearchRecommendResponse>({
    url: GET_SEARCH_RECOMMAND_LIST(),
    baseUrl: baseURL,
    options,
    data: {
      recommendItems,
      gender,
      keyword,
      custNo,
    },
  });
  // const { data } = await axiosDisplay.post<SearchRecommendResponse>(
  //   GET_SEARCH_RECOMMAND_LIST(),
  //   {
  //     recommendItems,
  //     gender,
  //     keyword,
  //     custNo,
  //   }
  // );
  return data;
};

const GET_SEARCH_RECOMMAND_EVENT_LIST = () =>
  `${ApiTypes.SEARCH}/result/issue/recommend`;

export const requestSearchRcommendEventList = async () => {
  const { data } = await get<SearchRecommendEventResponse>({
    url: GET_SEARCH_RECOMMAND_EVENT_LIST(),
    baseUrl: baseURL,
    options,
  });
  // const { data } = await axiosDisplay.get<SearchRecommendEventResponse>(
  //   GET_SEARCH_RECOMMAND_EVENT_LIST()
  // );
  return data;
};

export const GET_SEARCH_PRODCT_AGG = (type: string) =>
  `${ApiTypes.SEARCH}/result/product/aggs/${type}`;

export const requestSearchProdcutAgg = async (
  type: string,
  {
    gender,
    keyword,
    sort,
    pageNo = 1,
    pageSize,
    sq,
    bcds,
    mcd,
    ccds,
    pcds,
    lcds,
    priceSections,
    minPrice,
    maxPrice,
    colors,
    benefits,
    discounts,
    status,
    saleTags,
    source,
    device,
    searchType,
    recommendItems,
    aggs = [
      'brand',
      'topBrand',
      'newBrand',
      'category',
      'status',
      'discounts',
      'benefits',
      'recoCategory',
      'colors',
      'saleTag',
      'priceRange',
    ],
  }: RequestSearchAccParams
) => {
  const { data } = await post<SearchAggsResponse>({
    url: GET_SEARCH_PRODCT_AGG(type),
    baseUrl: baseURL,
    options,
    data: {
      gender,
      keyword,
      sort,
      pageNo,
      pageSize,
      sq,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      aggs,
      source,
      device,
      searchType,
      recommendItems,
    },
  });

  // const { data } = await axiosDisplay.post<SearchAggsResponse>(
  //   GET_SEARCH_PRODCT_AGG(type),
  //   {
  //     gender,
  //     keyword,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     sq,
  //     bcds,
  //     mcd,
  //     ccds,
  //     pcds,
  //     lcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     aggs,
  //     source,
  //     device,
  //     searchType,
  //     recommendItems,
  //   }
  // );
  return data;
};

/**
 * 검색결과 - 필터 (집계정보)
 */
export interface RequestSearchProdcutListParams {
  gender: string;
  keyword: string;
  custNo: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  sq?: null;
  bcds?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  priceSections?: number[];
  minPrice?: number;
  maxPrice?: number;
  colors?: string[];
  benefits?: string[];
  discounts?: string[];
  status?: string[];
  saleTags?: string[];
  aggs?: string[];
  source: string;
  device: 'Android' | 'iOS' | 'PC' | 'MO' | string;
  searchType?:
    | 'popular'
    | 'related'
    | 'recent'
    | 'direct'
    | 'ac_key'
    | 'ac_key_brand'
    | string;
  recommendItems?: string[];
}

export const GET_SEARCH_PRODCT_FILTER_NAME = () =>
  `${ApiTypes.SEARCH}/result/product/filter/name`;

export const requestSearchProdcutFilterName = async ({
  custNo,
  gender,
  keyword,
  sort,
  pageNo = 1,
  pageSize,
  sq,
  bcds,
  mcd,
  ccds,
  pcds,
  lcds,
  priceSections,
  minPrice,
  maxPrice,
  colors,
  benefits,
  discounts,
  status,
  saleTags,
  source,
  device,
  searchType,
  recommendItems,
}: RequestSearchFilterNameParams) => {
  const { data } = await post<SearchFilterNameResponse>({
    url: GET_SEARCH_PRODCT_FILTER_NAME(),
    baseUrl: baseURL,
    options,
    data: {
      custNo,
      gender,
      keyword,
      sort,
      pageNo,
      pageSize,
      sq,
      bcds,
      mcd,
      ccds,
      pcds,
      lcds,
      priceSections,
      minPrice,
      maxPrice,
      colors,
      benefits,
      discounts,
      status,
      saleTags,
      source,
      device,
      searchType,
      recommendItems,
    },
  });

  // const { data } = await axiosDisplay.post<SearchFilterNameResponse>(
  //   GET_SEARCH_PRODCT_FILTER_NAME(),
  //   {
  //     custNo,
  //     gender,
  //     keyword,
  //     sort,
  //     pageNo,
  //     pageSize,
  //     sq,
  //     bcds,
  //     mcd,
  //     ccds,
  //     pcds,
  //     lcds,
  //     priceSections,
  //     minPrice,
  //     maxPrice,
  //     colors,
  //     benefits,
  //     discounts,
  //     status,
  //     saleTags,
  //     source,
  //     device,
  //     searchType,
  //     recommendItems,
  //   }
  // );
  return data;
};

export const requestMolocoAdProductList = async ({
  id,
  pageId,
  searchQuery,
}: RequestMolocoAdProductsParam) => {
  try {
    const sessionId = await getCookieWckDisplayMktSID();
    const userId = await getMolocoCustNo(false);

    const { data } = await post<MolocoAdProductRespose>({
      url: `/display/api/v1/moloco/ad/products`,
      baseUrl: baseURL,
      options,
      data: {
        id,
        pageId,
        sessionId: sessionId ? encodeURIComponent(sessionId) : '',
        userId: userId || null,
        searchQuery,
      },
    });

    // const { data } = await axiosDisplay.post<MolocoAdProductRespose>(
    //   `/display/api/v1/moloco/ad/products`,
    //   {
    //     id,
    //     pageId,
    //     sessionId,
    //     userId,
    //     searchQuery,
    //   }
    // );
    return data;
  } catch (e: any) {
    return {
      result: 'error',
      errorCode: e?.status,
      message: '',
      data: {
        webPosition: 0,
        appPosition: 0,
        items: [],
        total: 0,
      },
    } as MolocoAdProductRespose;
  }
};

/**
 * 검색결과 내 단독 상품
 */
export interface SrchResultExclusivePrdRespose extends CommonResponse {
  data: SrchResultExclusivePrdResult;
}

export const requestSrchResultExclusivePrdList = async (keyword: string) => {
  try {
    const { data } = await post<SrchResultExclusivePrdRespose>({
      url: `/display/api/v3/search/result/exclusive/brand/product`,
      baseUrl: baseURL,
      options,
      data: {
        keyword,
      },
    });

    // const { data } = await axiosDisplay.post<SrchResultExclusivePrdRespose>(
    //   `/display/api/v3/search/result/exclusive/brand/product`,
    //   {
    //     keyword,
    //   }
    // );
    return data;
  } catch (e: any) {
    return {
      result: 'error',
      errorCode: e?.status,
      message: '',
      data: {
        webPosition: 0,
        appPosition: 0,
        brandsList: [],
        total: 0,
      },
    };
  }
};

/**
 * 검색결과 내 방근본 상품 추천
 */
export interface SrchRecentRecommendPrdRespose extends CommonResponse {
  data: RecommendResult;
}

export const requestSrchRecentRecommendPrdList = async (itemCd: string) => {
  const { data } = await get<SrchRecentRecommendPrdRespose>({
    url: `/display/api/v3/search/result/product/recommend/${itemCd}`,
    baseUrl: baseURL,
    options,
  });

  // const { data } = await axiosDisplay.get<SrchRecentRecommendPrdRespose>(
  //   `/display/api/v3/search/result/product/recommend/${itemCd}`
  // );
  return data;
};
